import type { ComponentPropsWithRef, ReactNode } from 'react';

import { Accordion } from '@base-ui/react';

import { clsx } from '~/styles/utils';

import type { IconName } from '../Icon/Icon';

import { Icon } from '../Icon/Icon';
import {
  activeIndicatorStyle,
  activeLabelStyle,
  containerStyle,
  contentStyle,
  hintStyle,
  iconStyle,
  indicatorStyle,
  itemStyle,
  labelStyle,
  panelStyle,
  textStyle,
  triggerStyle,
} from './Timeline.css';

type Props = Omit<ComponentPropsWithRef<'li'>, 'content'> & {
  children: ReactNode;
  content?: ReactNode;
  done?: boolean;
  hint?: string;
  icon: IconName;
};

export const TimelineItem = ({ children, content, done, hint, icon, ...delegated }: Props) => {
  if (!content) {
    return (
      <li {...delegated} className={clsx(containerStyle, delegated.className)}>
        <TimelineItemSummary done={done} hint={hint} icon={icon}>
          {children}
        </TimelineItemSummary>
      </li>
    );
  }

  return (
    <li {...delegated} className={clsx(containerStyle, delegated.className)}>
      <Accordion.Item>
        <Accordion.Header>
          <Accordion.Trigger className={triggerStyle}>
            <TimelineItemSummary done={done} hint={hint} icon={icon}>
              {children}
            </TimelineItemSummary>

            <Icon className={iconStyle} name="ChevronDown" />
          </Accordion.Trigger>
        </Accordion.Header>

        <Accordion.Panel className={panelStyle}>
          <div className={textStyle}>{content}</div>
        </Accordion.Panel>
      </Accordion.Item>
    </li>
  );
};

const TimelineItemSummary = ({ children, done, hint, icon }: Omit<Props, 'content'>) => (
  <div className={itemStyle}>
    <div className={clsx(indicatorStyle, done && activeIndicatorStyle)}>
      <Icon name={icon} />
    </div>

    <div className={contentStyle}>
      <span className={clsx(labelStyle, done && activeLabelStyle)}>{children}</span>

      {hint && <span className={hintStyle}>{hint}</span>}
    </div>
  </div>
);
